/**
 * Network Resilience Utility
 * 
 * Implements enterprise-grade network resilience patterns including
 * retry logic, circuit breaker, rate limiting, and security measures.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import { log } from './logger'

// ================================================================
// TYPES & INTERFACES
// ================================================================

export interface RetryConfig {
  maxAttempts: number
  baseDelay: number
  maxDelay: number
  backoffMultiplier: number
  jitter: boolean
  retryableErrors: string[]
  timeoutMs: number
}

export interface CircuitBreakerConfig {
  failureThreshold: number
  resetTimeoutMs: number
  monitoringPeriodMs: number
}

export interface RateLimitConfig {
  maxRequests: number
  windowMs: number
}

export interface NetworkConfig {
  retry: RetryConfig
  circuitBreaker: CircuitBreakerConfig
  rateLimit: RateLimitConfig
  securityHeaders: Record<string, string>
}

interface CircuitBreakerState {
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN'
  failureCount: number
  lastFailureTime: number
  nextAttemptTime: number
}

interface RateLimitState {
  requests: number[]
  windowStart: number
}

// ================================================================
// DEFAULT CONFIGURATIONS
// ================================================================

const DEFAULT_CONFIG: NetworkConfig = {
  retry: {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2,
    jitter: true,
    retryableErrors: [
      'TypeError: Load failed',
      'TypeError: Failed to fetch',
      'NetworkError',
      'TimeoutError',
      'AbortError'
    ],
    timeoutMs: 30000
  },
  circuitBreaker: {
    failureThreshold: 5,
    resetTimeoutMs: 60000,
    monitoringPeriodMs: 60000
  },
  rateLimit: {
    maxRequests: 100,
    windowMs: 60000
  },
  securityHeaders: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin'
  }
}

// ================================================================
// NETWORK RESILIENCE CLASS
// ================================================================

export class NetworkResilience {
  private config: NetworkConfig
  private circuitBreakerStates = new Map<string, CircuitBreakerState>()
  private rateLimitStates = new Map<string, RateLimitState>()

  constructor(config: Partial<NetworkConfig> = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      retry: { ...DEFAULT_CONFIG.retry, ...config.retry },
      circuitBreaker: { ...DEFAULT_CONFIG.circuitBreaker, ...config.circuitBreaker },
      rateLimit: { ...DEFAULT_CONFIG.rateLimit, ...config.rateLimit },
      securityHeaders: { ...DEFAULT_CONFIG.securityHeaders, ...config.securityHeaders }
    }
  }

  // ================================================================
  // RETRY LOGIC WITH EXPONENTIAL BACKOFF
  // ================================================================

  async withRetry<T>(
    operation: () => Promise<T>,
    context: string,
    customConfig?: Partial<RetryConfig>
  ): Promise<T> {
    const config = { ...this.config.retry, ...customConfig }
    let lastError: Error

    for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
      try {
        log.debug(`Network operation attempt ${attempt}/${config.maxAttempts}`, 'NetworkResilience', {
          context,
          attempt
        })

        const result = await this.withTimeout(operation(), config.timeoutMs)
        
        if (attempt > 1) {
          log.info(`Network operation succeeded after ${attempt} attempts`, 'NetworkResilience', {
            context,
            totalAttempts: attempt
          })
        }

        return result

      } catch (error) {
        lastError = error as Error
        
        log.warn(`Network operation attempt ${attempt} failed`, 'NetworkResilience', {
          context,
          attempt,
          error: lastError.message,
          maxAttempts: config.maxAttempts
        })

        // Check if error is retryable
        if (!this.isRetryableError(lastError, config.retryableErrors)) {
          log.error('Non-retryable error encountered', 'NetworkResilience', {
            context,
            error: lastError.message
          })
          throw lastError
        }

        // Don't delay after the last attempt
        if (attempt < config.maxAttempts) {
          const delay = this.calculateDelay(attempt, config)
          log.debug(`Waiting ${delay}ms before retry`, 'NetworkResilience', {
            context,
            delay,
            attempt
          })
          await this.sleep(delay)
        }
      }
    }

    log.error(`All ${config.maxAttempts} network attempts failed`, 'NetworkResilience', {
      context,
      finalError: lastError!.message
    })
    throw lastError!
  }

  // ================================================================
  // CIRCUIT BREAKER PATTERN
  // ================================================================

  async withCircuitBreaker<T>(
    operation: () => Promise<T>,
    circuitName: string,
    context: string
  ): Promise<T> {
    const state = this.getCircuitBreakerState(circuitName)

    // Check if circuit is open
    if (state.state === 'OPEN') {
      if (Date.now() < state.nextAttemptTime) {
        const error = new Error(`Circuit breaker is OPEN for ${circuitName}`)
        log.warn('Circuit breaker blocking request', 'NetworkResilience', {
          context,
          circuitName,
          state: state.state,
          nextAttempt: new Date(state.nextAttemptTime).toISOString()
        })
        throw error
      } else {
        // Transition to half-open
        state.state = 'HALF_OPEN'
        log.info('Circuit breaker transitioning to HALF_OPEN', 'NetworkResilience', {
          context,
          circuitName
        })
      }
    }

    try {
      const result = await operation()
      
      // Success - reset circuit breaker
      if (state.state === 'HALF_OPEN') {
        this.resetCircuitBreaker(circuitName)
        log.info('Circuit breaker reset to CLOSED', 'NetworkResilience', {
          context,
          circuitName
        })
      }
      
      return result

    } catch (error) {
      this.recordCircuitBreakerFailure(circuitName, context)
      throw error
    }
  }

  // ================================================================
  // RATE LIMITING
  // ================================================================

  async withRateLimit<T>(
    operation: () => Promise<T>,
    limitKey: string,
    context: string
  ): Promise<T> {
    if (!this.checkRateLimit(limitKey)) {
      const error = new Error(`Rate limit exceeded for ${limitKey}`)
      log.warn('Rate limit exceeded', 'NetworkResilience', {
        context,
        limitKey,
        maxRequests: this.config.rateLimit.maxRequests,
        windowMs: this.config.rateLimit.windowMs
      })
      throw error
    }

    try {
      const result = await operation()
      this.recordRequest(limitKey)
      return result
    } catch (error) {
      this.recordRequest(limitKey) // Still count failed requests
      throw error
    }
  }

  // ================================================================
  // COMBINED RESILIENCE WRAPPER
  // ================================================================

  async executeWithResilience<T>(
    operation: () => Promise<T>,
    options: {
      context: string
      circuitName?: string
      rateLimitKey?: string
      retryConfig?: Partial<RetryConfig>
    }
  ): Promise<T> {
    const { context, circuitName, rateLimitKey, retryConfig } = options

    let wrappedOperation = operation

    // Apply rate limiting if specified
    if (rateLimitKey) {
      const originalOp = wrappedOperation
      wrappedOperation = () => this.withRateLimit(originalOp, rateLimitKey, context)
    }

    // Apply circuit breaker if specified
    if (circuitName) {
      const originalOp = wrappedOperation
      wrappedOperation = () => this.withCircuitBreaker(originalOp, circuitName, context)
    }

    // Apply retry logic
    return this.withRetry(wrappedOperation, context, retryConfig)
  }

  // ================================================================
  // UTILITY METHODS
  // ================================================================

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Operation timeout')), timeoutMs)
    })

    return Promise.race([promise, timeoutPromise])
  }

  private isRetryableError(error: Error, retryableErrors: string[]): boolean {
    return retryableErrors.some(retryableError => 
      error.message.includes(retryableError) || 
      error.name.includes(retryableError)
    )
  }

  private calculateDelay(attempt: number, config: RetryConfig): number {
    let delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1)
    delay = Math.min(delay, config.maxDelay)

    // Add jitter to prevent thundering herd
    if (config.jitter) {
      delay = delay * (0.5 + Math.random() * 0.5)
    }

    return Math.floor(delay)
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private getCircuitBreakerState(circuitName: string): CircuitBreakerState {
    if (!this.circuitBreakerStates.has(circuitName)) {
      this.circuitBreakerStates.set(circuitName, {
        state: 'CLOSED',
        failureCount: 0,
        lastFailureTime: 0,
        nextAttemptTime: 0
      })
    }
    return this.circuitBreakerStates.get(circuitName)!
  }

  private recordCircuitBreakerFailure(circuitName: string, context: string): void {
    const state = this.getCircuitBreakerState(circuitName)
    state.failureCount++
    state.lastFailureTime = Date.now()

    if (state.failureCount >= this.config.circuitBreaker.failureThreshold) {
      state.state = 'OPEN'
      state.nextAttemptTime = Date.now() + this.config.circuitBreaker.resetTimeoutMs
      
      log.error('Circuit breaker opened due to failures', 'NetworkResilience', {
        context,
        circuitName,
        failureCount: state.failureCount,
        threshold: this.config.circuitBreaker.failureThreshold,
        nextAttempt: new Date(state.nextAttemptTime).toISOString()
      })
    }
  }

  private resetCircuitBreaker(circuitName: string): void {
    const state = this.getCircuitBreakerState(circuitName)
    state.state = 'CLOSED'
    state.failureCount = 0
    state.lastFailureTime = 0
    state.nextAttemptTime = 0
  }

  private checkRateLimit(limitKey: string): boolean {
    const now = Date.now()
    const state = this.getRateLimitState(limitKey)
    
    // Clean old requests outside the window
    state.requests = state.requests.filter(
      requestTime => requestTime > now - this.config.rateLimit.windowMs
    )

    return state.requests.length < this.config.rateLimit.maxRequests
  }

  private recordRequest(limitKey: string): void {
    const state = this.getRateLimitState(limitKey)
    state.requests.push(Date.now())
  }

  private getRateLimitState(limitKey: string): RateLimitState {
    if (!this.rateLimitStates.has(limitKey)) {
      this.rateLimitStates.set(limitKey, {
        requests: [],
        windowStart: Date.now()
      })
    }
    return this.rateLimitStates.get(limitKey)!
  }

  // ================================================================
  // SECURITY UTILITIES
  // ================================================================

  addSecurityHeaders(headers: HeadersInit = {}): HeadersInit {
    return {
      ...this.config.securityHeaders,
      ...headers
    }
  }

  sanitizeUrl(url: string): string {
    try {
      const parsedUrl = new URL(url)
      
      // Only allow HTTPS in production
      if (process.env.NODE_ENV === 'production' && parsedUrl.protocol !== 'https:') {
        throw new Error('Only HTTPS URLs are allowed in production')
      }

      return parsedUrl.toString()
    } catch (error) {
      log.error('Invalid URL provided', 'NetworkResilience', { url, error })
      throw new Error('Invalid URL format')
    }
  }

  // ================================================================
  // MONITORING & METRICS
  // ================================================================

  getMetrics() {
    const circuitBreakerMetrics = Array.from(this.circuitBreakerStates.entries()).map(
      ([name, state]) => ({
        circuitName: name,
        state: state.state,
        failureCount: state.failureCount,
        lastFailureTime: state.lastFailureTime
      })
    )

    const rateLimitMetrics = Array.from(this.rateLimitStates.entries()).map(
      ([key, state]) => ({
        limitKey: key,
        currentRequests: state.requests.length,
        maxRequests: this.config.rateLimit.maxRequests
      })
    )

    return {
      circuitBreakers: circuitBreakerMetrics,
      rateLimits: rateLimitMetrics,
      config: this.config
    }
  }
}

// ================================================================
// SINGLETON INSTANCE
// ================================================================

export const networkResilience = new NetworkResilience()

// ================================================================
// UTILITY FUNCTIONS
// ================================================================

export const withNetworkResilience = <T>(
  operation: () => Promise<T>,
  context: string,
  options: Partial<{
    circuitName: string
    rateLimitKey: string
    retryConfig: Partial<RetryConfig>
  }> = {}
) => {
  return networkResilience.executeWithResilience(operation, {
    context,
    ...options
  })
} 